/**
 * Trade Statistics API Service
 * Handles all trade statistics-related API calls to the backend
 */

import { authenticatedFetch } from "./apiClient";
import { appendTradePeriodLocalParams } from "../utils/notificationPeriod";
import type { TradePeriodLocalRangeQuery } from "../types/tradePeriodApiContract";
import { throwIfNotOk } from "../utils/apiRequestError";
import { PaywallRequiredError } from "../utils/paywallErrors";
import { parseApiErrorBody } from "../utils/parseApiError";
import { parseMarketDataAssetClassRequiredFromApiError } from "../utils/marketDataResolution";
import { isExecutionCandlesBackfillAcceptedResponse } from "../utils/executionCandlesStatus";
import {
  EXECUTION_CANDLES_TRADE_IDS_HARD_MAX,
  EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP,
} from "../utils/executionCandlesParams";
import {
  BackendTradeStatistics,
  SymbolStatisticsResponse,
  TradeStatisticsFilters,
  PnlOverTimeResponse,
  DistributionResponse,
  RiskMetricsResponse,
  MonthlyPerformanceResponse,
  WeeklyBreakdownResponse,
  DailyActivityResponse,
  ComparativeAnalysisResponse,
  TimeAnalysisResponse,
  ExecutionCandlesResponse,
  SessionPerformanceResponse,
  OhlcStripPollResponse,
} from "../types/trade";

/**
 * Fetch trade statistics from backend
 */
export const fetchTradeStatistics = async (
  filters?: TradeStatisticsFilters
): Promise<BackendTradeStatistics> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/statistics?${queryString}` : "/trades/statistics";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch trade statistics: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch symbol-specific statistics from backend
 */
export const fetchSymbolStatistics = async (
  filters?: Omit<TradeStatisticsFilters, "symbol">
): Promise<SymbolStatisticsResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString
    ? `/trades/statistics/by-symbol?${queryString}`
    : "/trades/statistics/by-symbol";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch symbol statistics: ${response.status}`);
  }

  return await response.json();
};

/**
 * Helper function to build query params from filters
 */
const buildQueryParams = (filters?: TradeStatisticsFilters): URLSearchParams => {
  const params = new URLSearchParams();

  const hasLocalRange =
    filters?.local_start_date != null &&
    filters?.local_end_date != null &&
    String(filters.local_start_date).length > 0 &&
    String(filters.local_end_date).length > 0;

  if (hasLocalRange) {
    const period: TradePeriodLocalRangeQuery = {
      local_start_date: filters!.local_start_date!,
      local_end_date: filters!.local_end_date!,
    };
    if (filters?.timezone) {
      period.timezone = filters.timezone;
    }
    appendTradePeriodLocalParams(params, period);
  } else {
    if (filters?.timeframe) {
      params.append("timeframe", filters.timeframe);
    }
    if (filters?.start_date) {
      params.append("start_date", filters.start_date.toISOString());
    }
    if (filters?.end_date) {
      params.append("end_date", filters.end_date.toISOString());
    }
  }
  // Explicit IANA for timeframe / ISO paths (local range sets timezone via appendTradePeriodLocalParams)
  if (filters?.timezone && !hasLocalRange) {
    params.append("timezone", filters.timezone);
  }
  // New: Previous period dates (optional - backend auto-calculates if not provided)
  if (filters?.previous_start_date) {
    params.append("previous_start_date", filters.previous_start_date.toISOString());
  }
  if (filters?.previous_end_date) {
    params.append("previous_end_date", filters.previous_end_date.toISOString());
  }
  if (filters?.broker_account_code) {
    params.append("broker_account_code", filters.broker_account_code);
  }
  if (filters?.broker_name) {
    params.append("broker_name", filters.broker_name.toLowerCase());
  }
  if (filters?.symbol) {
    params.append("symbol", filters.symbol);
  }
  if (filters?.status && filters.status !== "all") {
    params.append("status", filters.status);
  }
  if (filters?.fields) {
    params.append("fields", filters.fields);
  }
  if (filters?.period) {
    params.append("period", filters.period);
  }

  if (filters?.trade_ids) {
    params.append("trade_ids", filters.trade_ids.join(","));
  }

  if (filters?.trade_type) {
    params.append("trade_type", filters.trade_type);
  }

  if (filters?.profit_loss) {
    params.append("profit_loss", filters.profit_loss);
  }

  return params;
};

/** Cumulative transfer components at a point (or window summary). Omitted for `trading_pnl_only`. */
export interface PortfolioGrowthTransferBreakdown {
  cumulative_deposits: number;
  cumulative_withdrawals: number;
  cumulative_financing: number;
  cumulative_commissions: number;
  net_transfers: number;
}

/** Period delta of transfer components vs previous chart point. */
export interface PortfolioGrowthDailyTransferBreakdown {
  deposits: number;
  withdrawals: number;
  financing: number;
  commissions: number;
  net: number;
}

/**
 * Portfolio Growth Data Point
 * Each data point represents portfolio value at a specific point in time
 * Backend should return an array of these points to draw the chart
 */
export interface PortfolioGrowthDataPoint {
  date: string;              // "YYYY-MM-DD" | "YYYY-MM-DD HH:00" (hourly) | "YYYY-MM" (monthly)
  /** Closing equity; null when `growth_basis` is `trading_pnl_only` (use `cumulative_pnl` for charts). */
  portfolio_value?: number | null;
  opening_balance?: number;  // Portfolio value at START of this date/period (opening balance)
  cumulative_pnl: number;    // Cumulative P&L up to this date (true trading P&L, excludes transfers)
  cumulative_transfers?: number; // Net deposits + withdrawals + financing (excludes commissions)
  daily_pnl: number;         // NEW: P&L for this specific day/period
  daily_transfers?: number;   // NEW: Transfers (deposits - withdrawals) for this specific day/period
  trade_count: number;       // Number of trades closed on this date/period
  transfer_breakdown?: PortfolioGrowthTransferBreakdown;
  daily_transfer_breakdown?: PortfolioGrowthDailyTransferBreakdown;
}

/**
 * Portfolio Growth Response
 * MUST include data_points array with time series data for chart rendering
 * If data_points is empty or missing, chart cannot be drawn
 */
export interface PortfolioGrowthResponse {
  data_points: PortfolioGrowthDataPoint[];  // REQUIRED: Array of time series data points
  starting_balance: number;                 // TRUE starting balance (excluding transfers)
  current_value: number;                    // Current portfolio value (last data point)
  true_pnl?: number;                         // True trading P&L (excluding transfers)
  net_transfers?: number;                    // Net deposits + withdrawals + financing for date range
  transfer_breakdown?: PortfolioGrowthTransferBreakdown;
  total_change_percent?: number | null;      // Total change as percentage (based on true_pnl)
  total_change_amount: number;              // Total P&L amount (same as true_pnl)
  /** Server-resolved mode e.g. trading_pnl_only vs portfolio_value_and_transfers */
  growth_basis?: "portfolio_value_and_transfers" | "trading_pnl_only" | string;
  growth_basis_note?: string;
  /** Effective bucket size after BE auto-upgrade (e.g. daily → hourly for today). */
  sampling?: "hourly" | "daily" | "weekly" | "monthly" | string;
}

/**
 * Cash Transfer interface
 * Represents deposits, withdrawals, and other cash transfers
 */
export interface CashTransfer {
  id: string;                               // UUID
  user_id: string;                          // Clerk user ID
  broker_account_code: string;              // Account code
  transfer_code: string;                    // Unique broker transfer ID
  transfer_type: 'DEPOSIT' | 'WITHDRAWAL' | 'COMMISSION' | 'FINANCING' | 'COST' | 'SETTLEMENT' | 'EX_DIVIDEND' | 'REBATE' | 'NEGATIVE_BALANCE_CORRECTION' | 'BUST';
  amount: number;                           // Positive for deposits, negative for withdrawals
  currency: string;                          // "USD"
  transaction_time: string;                 // ISO 8601 datetime (UTC)
  comment?: string;                          // Optional comment
  version?: number;                          // Optional broker version
  client_order_id?: string;                 // Optional client order ID
  broker_synced: boolean;                   // Always true (from broker)
  created_at: string;                       // ISO 8601 datetime
  updated_at: string;                        // ISO 8601 datetime
}

/**
 * Cash Transfers Response
 */
export interface CashTransfersResponse {
  transfers: CashTransfer[];
  total: number;                            // Number of transfers returned
}

/**
 * Win Rate Trend Data Point
 * Each data point represents cumulative win rate at a specific point in time
 * Backend should return an array of these points to draw the chart
 */
export interface WinRateTrendDataPoint {
  date: string;                // "YYYY-MM-DD" | "YYYY-MM-DD HH:00" (hourly) | "YYYY-MM" (monthly)
  cumulative_win_rate: number; // Cumulative win rate percentage (0-100) at this date
  cumulative_wins: number;     // Total wins up to this date
  cumulative_total: number;    // Total trades up to this date
  period_wins: number;         // Wins in this period (for this date/period only)
  period_total: number;        // Total trades in this period (for this date/period only)
  period_win_rate: number;     // Win rate for this period only (0-100)
}

/**
 * Win Rate Trend Response
 * MUST include data_points array with time series data for chart rendering
 * If data_points is empty or missing, chart cannot be drawn
 */
export interface WinRateTrendResponse {
  data_points: WinRateTrendDataPoint[];  // REQUIRED: Array of time series data points
  current_win_rate: number;              // Current overall win rate (last data point)
  /** Effective bucket size after BE auto-upgrade (e.g. daily → hourly for today). */
  period: string;                        // "hourly" | "daily" | "monthly"
  win_rate_change_percent: number | null; // Win rate change percentage for the selected timeframe
}

/**
 * Fetch portfolio growth data from backend
 */
export const fetchPortfolioGrowth = async (
  brokerAccountCode: string,
  options?: {
    sampling?: "hourly" | "daily" | "weekly" | "monthly";
    startDate?: Date;
    endDate?: Date;
    timeframe?: "today" | "7-days" | "14-days" | "30-days" | "all-time";
    startingBalance?: number;
    /** IANA zone — aligns with Display Preferences; busts client cache when TZ changes */
    timezone?: string;
    broker_name?: string;
    /**
     * When `supports_portfolio_growth` is false, send `trading_pnl_only` (or omit) — do not request
     * full portfolio/equity basis or the API may return 400.
     */
    growth_basis?: string;
  }
): Promise<PortfolioGrowthResponse> => {
  const params = new URLSearchParams();
  
  params.append("broker_account_code", brokerAccountCode);
  if (options?.broker_name) {
    params.append("broker_name", options.broker_name.toLowerCase());
  }
  
  if (options?.sampling) {
    params.append("sampling", options.sampling);
  }
  if (options?.startDate) {
    params.append("start_date", options.startDate.toISOString());
  }
  if (options?.endDate) {
    params.append("end_date", options.endDate.toISOString());
  }
  if (options?.timeframe) {
    params.append("timeframe", options.timeframe);
  }
  if (options?.timezone) {
    params.append("timezone", options.timezone);
  }
  if (options?.startingBalance !== undefined) {
    params.append("starting_balance", options.startingBalance.toString());
  }
  if (options?.growth_basis) {
    params.append("growth_basis", options.growth_basis);
  }

  const endpoint = `/trades/portfolio-growth?${params.toString()}`;
  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch portfolio growth: ${response.status}`);
  }

  const result: PortfolioGrowthResponse = await response.json();
  
  // Validate response structure
  if (!result.data_points || !Array.isArray(result.data_points)) {
    console.error("Invalid Portfolio Growth response: missing or invalid data_points array", result);
    throw new Error("Invalid response: missing data_points array. Backend must return time series data.");
  }
  
  if (result.data_points.length === 0) {
    console.warn("Portfolio Growth: Empty data_points array. No historical data available.");
  }
  
  return result;
};

/**
 * Fetch win rate trend data from backend
 */
export const fetchWinRateTrend = async (
  brokerAccountCode: string,
  options?: {
    period?: "hourly" | "daily" | "monthly";
    /** ISO instant bounds (mutually exclusive with local_*). */
    startDate?: Date;
    endDate?: Date;
    timeframe?: "today" | "7-days" | "14-days" | "30-days" | "all-time";
    /** Local calendar bounds (recommended; mutually exclusive with startDate/endDate). */
    local_start_date?: string;
    local_end_date?: string;
    timezone?: string;
    broker_name?: string;
  }
): Promise<WinRateTrendResponse> => {
  const params = new URLSearchParams();
  
  params.append("broker_account_code", brokerAccountCode);
  if (options?.broker_name) {
    params.append("broker_name", options.broker_name.toLowerCase());
  }
  
  if (options?.period) {
    params.append("period", options.period);
  }

  const hasLocal =
    typeof options?.local_start_date === "string" &&
    options.local_start_date.trim() !== "" &&
    typeof options?.local_end_date === "string" &&
    options.local_end_date.trim() !== "";
  if (hasLocal) {
    params.append("local_start_date", options!.local_start_date!.trim());
    params.append("local_end_date", options!.local_end_date!.trim());
    if (options?.timezone) params.append("timezone", options.timezone);
  } else {
    if (options?.startDate) {
      params.append("start_date", options.startDate.toISOString());
    }
    if (options?.endDate) {
      params.append("end_date", options.endDate.toISOString());
    }
    if (options?.timeframe) {
      params.append("timeframe", options.timeframe);
    }
    if (options?.timezone) {
      params.append("timezone", options.timezone);
    }
  }

  const endpoint = `/trades/win-rate-trend?${params.toString()}`;
  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch win rate trend: ${response.status}`);
  }

  const result: WinRateTrendResponse = await response.json();
  
  // Validate response structure
  if (!result.data_points || !Array.isArray(result.data_points)) {
    console.error("Invalid Win Rate Trend response: missing or invalid data_points array", result);
    throw new Error("Invalid response: missing data_points array. Backend must return time series data.");
  }
  
  if (result.data_points.length === 0) {
    console.warn("Win Rate Trend: Empty data_points array. No historical data available.");
  }
  
  return result;
};

/**
 * Fetch P&L over time data from backend
 */
export const fetchPnLOverTime = async (
  filters?: TradeStatisticsFilters,
  breakdown: "hourly" | "daily" | "monthly" | "weekly" = "monthly"
): Promise<PnlOverTimeResponse> => {
  const params = buildQueryParams(filters);
  params.append("breakdown", breakdown);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/pnl-over-time?${queryString}` : `/trades/pnl-over-time?breakdown=${breakdown}`;

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch P&L over time: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch distribution data from backend
 */
export const fetchDistribution = async (
  filters?: TradeStatisticsFilters
): Promise<DistributionResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/distribution?${queryString}` : "/trades/distribution";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch distribution: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch risk metrics from backend
 */
export const fetchRiskMetrics = async (
  filters?: TradeStatisticsFilters
): Promise<RiskMetricsResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/risk-metrics?${queryString}` : "/trades/risk-metrics";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch risk metrics: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch monthly performance data from backend
 */
export const fetchMonthlyPerformance = async (
  filters?: TradeStatisticsFilters
): Promise<MonthlyPerformanceResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/monthly-performance?${queryString}` : "/trades/monthly-performance";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch monthly performance: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch weekly breakdown data from backend
 */
export const fetchWeeklyBreakdown = async (
  filters?: TradeStatisticsFilters
): Promise<WeeklyBreakdownResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/weekly-breakdown?${queryString}` : "/trades/weekly-breakdown";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch weekly breakdown: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch daily activity data from backend
 */
export const fetchDailyActivity = async (
  filters?: TradeStatisticsFilters
): Promise<DailyActivityResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/daily-activity?${queryString}` : "/trades/daily-activity";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch daily activity: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch comparative analysis data from backend
 */
export const fetchComparativeAnalysis = async (
  filters?: TradeStatisticsFilters
): Promise<ComparativeAnalysisResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/comparative-analysis?${queryString}` : "/trades/comparative-analysis";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch comparative analysis: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch time analysis data from backend
 */
export const fetchTimeAnalysis = async (
  filters?: TradeStatisticsFilters
): Promise<TimeAnalysisResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString ? `/trades/time-analysis?${queryString}` : "/trades/time-analysis";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch time analysis: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch cash transfers (deposits, withdrawals, etc.) from backend
 */
export const fetchCashTransfers = async (
  brokerAccountCode: string,
  options?: {
    startDate?: Date;
    endDate?: Date;
    transferTypes?: string;         // Comma-separated: "DEPOSIT,WITHDRAWAL"
    limit?: number;
    offset?: number;
  }
): Promise<CashTransfersResponse> => {
  const params = new URLSearchParams();
  
  params.append("broker_account_code", brokerAccountCode);
  
  if (options?.startDate) {
    params.append("start_date", options.startDate.toISOString());
  }
  if (options?.endDate) {
    params.append("end_date", options.endDate.toISOString());
  }
  if (options?.transferTypes) {
    params.append("transfer_types", options.transferTypes);
  }
  if (options?.limit !== undefined) {
    params.append("limit", options.limit.toString());
  }
  if (options?.offset !== undefined) {
    params.append("offset", options.offset.toString());
  }

  const endpoint = `/trades/cash-transfers?${params.toString()}`;
  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch cash transfers: ${response.status}`);
  }

  return await response.json();
};

/**
 * Dashboard Summary Response
 * Lightweight response containing only the data needed for the dashboard
 */
/** Timeframe keys accepted by `/dashboard/summary` per-card query params */
export type DashboardSummaryCardTimeframe =
  | "today"
  | "7-days"
  | "14-days"
  | "30-days"
  | "all-time";

export type DashboardSummaryTimeframesInput = {
  portfolio?: DashboardSummaryCardTimeframe;
  winRate?: DashboardSummaryCardTimeframe;
  pnl?: DashboardSummaryCardTimeframe;
  portfolioGrowth?: DashboardSummaryCardTimeframe;
  winRateTrend?: DashboardSummaryCardTimeframe;
  roi?: DashboardSummaryCardTimeframe;
  /**
   * When both set, dashboard metrics use this window; per-card `*_timeframe` params are omitted.
   * (Aligns Trade Log / Statistics / Reports custom ranges with the home dashboard.)
   */
  start_date?: string;
  end_date?: string;
  /** Inclusive civil dates for the picker window (preferred by backend when set). */
  local_start_date?: string;
  local_end_date?: string;
  timezone?: string;
};

/** Aligned with P&L card window (`pnl_timeframe` or custom `start_date`/`end_date`). */
export interface DashboardTradingMetrics {
  profit_factor: number | null;
  expectancy: number | null;
  max_drawdown: number | null;
  max_drawdown_percent: number | null;
  closed_trades_count: number;
  scope_timeframe: string;
  scope_start?: string;
  scope_end?: string;
}

export interface DashboardSummaryResponse {
  portfolio: {
    portfolio_value: number;
    portfolio_change_percent: number | null;
  };
  pnl: {
    total_pnl: number;
    pnl_change_percent: number | null;
    closed_trades?: number;
    open_trades?: number;
    winning_trades?: number;
    losing_trades?: number;
  };
  winRate: {
    win_rate: number;
    win_rate_change_percent: number | null;
  };
  roi: {
    total_invested: number;
    roi_percent: number | null;
  };
  portfolioGrowth: PortfolioGrowthResponse;
  winRateTrend: WinRateTrendResponse;
  tradingMetrics?: DashboardTradingMetrics;
  recentTrades: Array<{
    id: string;
    symbol: string;
    type: "buy" | "sell";
    status: "open" | "closed";
    entryDate: string;
    entryPrice: number;
    exitPrice?: number;
    exitDate?: string;
    exit_date?: string;
    netPnL?: number;
    returnPercent?: number;
    entry_quantity?: number;
  }>;
}

/**
 * Fetch dashboard summary from backend
 * Single lightweight endpoint that returns all dashboard data in one call
 */
export const fetchDashboardSummary = async (
  brokerAccountCode: string,
  timeframes: DashboardSummaryTimeframesInput,
  brokerName?: string | null
): Promise<DashboardSummaryResponse> => {
  const params = new URLSearchParams();
  
  params.append("broker_account_code", brokerAccountCode);
  if (brokerName) {
    params.append("broker_name", brokerName.toLowerCase());
  }

  if (timeframes.start_date && timeframes.end_date) {
    params.append("start_date", timeframes.start_date);
    params.append("end_date", timeframes.end_date);
    if (timeframes.local_start_date) {
      params.append("local_start_date", timeframes.local_start_date);
    }
    if (timeframes.local_end_date) {
      params.append("local_end_date", timeframes.local_end_date);
    }
    if (timeframes.timezone) {
      params.append("timezone", timeframes.timezone);
    }
    if (timeframes.roi) {
      params.append("roi_timeframe", timeframes.roi);
    }
  } else {
    if (timeframes.portfolio) {
      params.append("portfolio_timeframe", timeframes.portfolio);
    }
    if (timeframes.winRate) {
      params.append("win_rate_timeframe", timeframes.winRate);
    }
    if (timeframes.pnl) {
      params.append("pnl_timeframe", timeframes.pnl);
    }
    if (timeframes.portfolioGrowth) {
      params.append("portfolio_growth_timeframe", timeframes.portfolioGrowth);
    }
    if (timeframes.winRateTrend) {
      params.append("win_rate_trend_timeframe", timeframes.winRateTrend);
    }
    if (timeframes.roi) {
      params.append("roi_timeframe", timeframes.roi);
    }
  }

  const endpoint = `/dashboard/summary?${params.toString()}`;
  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    if (response.status === 402) {
      throw new PaywallRequiredError();
    }
    await throwIfNotOk(response, `Failed to fetch dashboard summary: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch execution candles + trade overlays for Trade Review.
 * Use {@link loadExecutionCandles} in UI — it handles 202 async backfill + poll.
 *
 * Endpoint: GET /api/trades/execution-candles
 * (Use `/trades/execution-candles` here because apiClient prefixes `/api`.)
 */
/**
 * Initial GET can block while stats-service loads trades + OHLC (no 202 when DB coverage ≥85%).
 * BE app→stats proxy allows up to ~300s; use a generous client timeout so heavy report cohorts
 * (500+ trades, all-time) complete before abort. Async backfill (202) returns quickly regardless.
 */
const EXECUTION_CANDLES_REQUEST_TIMEOUT_MS = 180_000;

export class ExecutionCandlesApiError extends Error {
  status: number;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ExecutionCandlesApiError";
    this.status = status;
    this.body = body;
  }
}

/** Thrown when BE returns 202 — caller should poll then retry (see loadExecutionCandles). */
export class ExecutionCandlesBackfillAccepted extends Error {
  status = 202 as const;
  body: ExecutionCandlesResponse;

  constructor(body: ExecutionCandlesResponse) {
    super("Execution candles backfill in progress");
    this.name = "ExecutionCandlesBackfillAccepted";
    this.body = body;
  }
}

export type ExecutionCandlesFetchParams = {
  symbol: string;
  broker_account_code: string;
  broker_name?: string;
  /** Single calendar day YYYY-MM-DD. */
  date?: string;
  timeframe?: "today" | "7-days" | "14-days" | "30-days" | "all-time" | string;
  /** Explicit ISO window (chunk from `suggested_periods` or custom range). */
  start_date?: string;
  end_date?: string;
  /** Local calendar window (preferred when mirroring trade list / stats). */
  local_start_date?: string;
  local_end_date?: string;
  reporting_time_zone?: string;
  candle_type?: string;
  fill_missing?: boolean;
  /**
   * When false, omit journal fills from the chart (Strategy R&D research windows).
   * Omit the field to keep Trade Review default (include fills).
   */
  include_trades?: boolean;
  /**
   * Explicit OHLC lane pin. When set, execution-candles skips preference /
   * executing-broker heuristics (Strategy R&D match.chart).
   */
  market_data_broker?: string;
  md_source?: string;
  md_listing_key?: string;
  /** Trade UUIDs — fallback for exact cohort only; never with report_id. */
  trade_ids?: string[];
  focus_trade_id?: string;
  asset_class?: string;
  backtest_id?: string;
  report_id?: string;
  /** Demo Entry Analysis job id — uses demo execution-candles (poll cookie). */
  demo_job_id?: string;
  /** Opt-in bar-aligned regime condition series (default off). */
  include_regime_overlay?: boolean;
  /**
   * Comma-separated overlay kinds (regime,direction,sma,bb,…).
   * Prefer this for multi-overlay; regime still also accepts include_regime_overlay.
   */
  include_overlays?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
};

function hasExecutionCandlesWindow(params: ExecutionCandlesFetchParams): boolean {
  const hasExplicit = Boolean(params.start_date && params.end_date);
  const hasLocal =
    Boolean(params.local_start_date && params.local_end_date);
  const hasTimeframe = Boolean(params.timeframe);
  const hasDate = Boolean(params.date);
  return hasExplicit || hasLocal || hasTimeframe || hasDate;
}

export function buildExecutionCandlesQueryString(params: ExecutionCandlesFetchParams): string {
  if (!hasExecutionCandlesWindow(params)) {
    throw new Error(
      "Execution candles require date, timeframe, start_date/end_date, or local_start_date/local_end_date."
    );
  }

  const qs = new URLSearchParams();
  qs.append("symbol", params.symbol);
  qs.append("broker_account_code", params.broker_account_code);
  if (params.broker_name) {
    qs.append("broker_name", params.broker_name.toLowerCase());
  }

  const hasExplicit = Boolean(params.start_date && params.end_date);
  const hasLocal = Boolean(params.local_start_date && params.local_end_date);

  if (params.date) {
    qs.append("date", params.date);
  } else if (hasExplicit) {
    qs.append("start_date", params.start_date!);
    qs.append("end_date", params.end_date!);
  } else if (hasLocal) {
    const period: TradePeriodLocalRangeQuery = {
      local_start_date: params.local_start_date!,
      local_end_date: params.local_end_date!,
    };
    if (params.reporting_time_zone) {
      period.timezone = params.reporting_time_zone;
    }
    appendTradePeriodLocalParams(qs, period);
  } else if (params.timeframe) {
    qs.append("timeframe", params.timeframe);
  }

  if (params.reporting_time_zone && !hasLocal) {
    qs.append("timezone", params.reporting_time_zone);
  }
  if (params.candle_type) qs.append("candle_type", params.candle_type);
  if (params.fill_missing) qs.append("fill_missing", "true");
  if (params.include_trades === false) {
    qs.append("include_trades", "false");
  } else if (params.include_trades === true) {
    qs.append("include_trades", "true");
  }
  if (params.market_data_broker?.trim()) {
    qs.append("market_data_broker", params.market_data_broker.trim());
  }
  if (params.md_source?.trim()) {
    qs.append("md_source", params.md_source.trim());
  }
  if (params.md_listing_key?.trim()) {
    qs.append("md_listing_key", params.md_listing_key.trim());
  }
  if (params.trade_ids && params.trade_ids.length > 0) {
    if (params.trade_ids.length > EXECUTION_CANDLES_TRADE_IDS_HARD_MAX) {
      throw new Error(
        `execution-candles: trade_ids length ${params.trade_ids.length} exceeds hard max ${EXECUTION_CANDLES_TRADE_IDS_HARD_MAX}; use backtest_id or report_id`
      );
    }
    if (params.trade_ids.length > EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP) {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn(
          `[execution-candles] trade_ids=${params.trade_ids.length} exceeds soft cap ${EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP}; prefer backtest_id/report_id`
        );
      }
    }
    qs.append("trade_ids", params.trade_ids.join(","));
  }
  if (params.report_id) qs.append("report_id", params.report_id);
  if (params.focus_trade_id) qs.append("focus_trade_id", params.focus_trade_id);
  if (params.asset_class) qs.append("asset_class", params.asset_class);
  if (params.backtest_id) qs.append("backtest_id", params.backtest_id);
  if (params.include_regime_overlay) qs.append("include_regime_overlay", "true");
  if (params.include_overlays?.trim()) {
    qs.append("include_overlays", params.include_overlays.trim());
  }

  return qs.toString();
}

function formatFastApiErrorDetail(body: unknown): string {
  const parsed = parseApiErrorBody(
    body && typeof body === "object" ? (body as unknown as Record<string, unknown>) : {},
    "Invalid execution candles request."
  );
  return parsed.message || "Invalid execution candles request.";
}

export const fetchExecutionCandles = async (
  params: ExecutionCandlesFetchParams
): Promise<ExecutionCandlesResponse> => {
  if (params.demo_job_id?.trim() && params.trade_ids?.length) {
    const { fetchDemoExecutionCandles } = await import("./demoEntryAnalysisApi");
    return await fetchDemoExecutionCandles({
      jobId: params.demo_job_id.trim(),
      tradeIds: params.trade_ids,
      candleType: params.candle_type,
      includeRegimeOverlay: Boolean(params.include_regime_overlay),
      includeOverlays: params.include_overlays,
      signal: params.signal,
    });
  }

  const endpoint = `/trades/execution-candles?${buildExecutionCandlesQueryString(params)}`;

  const timeoutMs = params.timeoutMs ?? EXECUTION_CANDLES_REQUEST_TIMEOUT_MS;
  const timeoutController = new AbortController();
  const timeoutId = window.setTimeout(() => timeoutController.abort(), timeoutMs);

  const onAbort = () => timeoutController.abort();
  if (params.signal) {
    if (params.signal.aborted) timeoutController.abort();
    else params.signal.addEventListener("abort", onAbort, { once: true });
  }

  let response: Response;
  try {
    response = await authenticatedFetch(endpoint, { signal: timeoutController.signal });
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      if (params.signal?.aborted) throw err;
      throw new Error(
        "Chart data request timed out before the server finished. Large report or all-time windows can take 1–2 minutes — try again, pick a coarser candle type, or use a smaller chart period."
      );
    }
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    if (params.signal) params.signal.removeEventListener("abort", onAbort);
  }

  const body = (await response.json().catch(() => ({}))) as ExecutionCandlesResponse & {
    detail?: string;
    error?: string;
  };

  if (response.status === 202) {
    throw new ExecutionCandlesBackfillAccepted(body);
  }

  if (response.ok && isExecutionCandlesBackfillAcceptedResponse(body)) {
    throw new ExecutionCandlesBackfillAccepted(body);
  }

  if (!response.ok) {
    const parsed = parseApiErrorBody(
      body && typeof body === "object" ? (body as unknown as Record<string, unknown>) : {},
      `Failed to fetch execution candles: ${response.status}`
    );
    if (response.status === 401) {
      throw new ExecutionCandlesApiError(parsed.message, 401, body);
    }
    if (response.status === 422) {
      if (parseMarketDataAssetClassRequiredFromApiError(body)) {
        throw new ExecutionCandlesApiError(
          "Market data asset class confirmation is required for this symbol.",
          422,
          body
        );
      }
      throw new ExecutionCandlesApiError(parsed.message, 422, body);
    }
    throw new ExecutionCandlesApiError(parsed.message, response.status, body);
  }

  return body;
};

/** Poll OHLC strip progress while async backfill runs. */
export const fetchOhlcStrip = async (
  pollUrl: string,
  signal?: AbortSignal
): Promise<OhlcStripPollResponse> => {
  const path = pollUrl.startsWith("/api/")
    ? pollUrl.slice(4)
    : pollUrl.startsWith("/")
      ? pollUrl
      : `/${pollUrl}`;

  const response = await authenticatedFetch(path, { signal });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const parsed = parseApiErrorBody(
      body && typeof body === "object" ? (body as unknown as Record<string, unknown>) : {},
      `Failed to poll OHLC strip: ${response.status}`
    );
    throw new ExecutionCandlesApiError(parsed.message, response.status, body);
  }

  return body;
};

/**
 * Fetch session performance statistics from backend
 */
export const fetchSessionPerformance = async (
  filters?: TradeStatisticsFilters
): Promise<SessionPerformanceResponse> => {
  const params = buildQueryParams(filters);

  const queryString = params.toString();
  const endpoint = queryString
    ? `/trades/statistics/by-session?${queryString}`
    : "/trades/statistics/by-session";

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to fetch session performance: ${response.status}`);
  }

  return await response.json();
};
